// Registro oficial de documentos emitidos por MERCALIA Connect Group LLC.
// Cada documento emitido con QR/enlace de verificación debe tener una entrada aquí.
// Tras agregar o modificar entradas: npm run build + deploy a Netlify.

export type DocStatus = 'valid' | 'superseded' | 'revoked' | 'expired';

export type VerifiedDocument = {
  /** Segmento de URL: mercaliacg.com/verify/<id>/ (usar MAYÚSCULAS y guiones) */
  id: string;
  /** Referencia impresa en el documento */
  ref: string;
  type: { es: string; en: string };
  issueDate: string; // YYYY-MM-DD
  status: DocStatus;
  /** Destinatario, si el documento fue emitido a una contraparte específica */
  issuedTo?: string;
  notes?: { es: string; en: string };
  /** Ruta pública al documento original (PDF) para mostrarlo tal como fue emitido */
  file?: string;
};

export const DOCUMENTS: Record<string, VerifiedDocument> = {
  'PT-MCG-2026-001': {
    id: 'PT-MCG-2026-001',
    ref: 'PT/MCG/2026-001',
    type: {
      es: 'Presentación Preliminar de Inversión — Proyecto PATAGONIA TROUT',
      en: 'Preliminary Investment Presentation — PATAGONIA TROUT Project',
    },
    issueDate: '2026-07-16',
    status: 'valid',
    notes: {
      es: 'Presentación preliminar del proyecto PATAGONIA TROUT emitida bajo sello corporativo electrónico para evaluación por un socio inversor financiero. Documento confidencial; no constituye oferta de valores. Los términos definitivos se establecen exclusivamente en los acuerdos entre las partes.',
      en: 'Preliminary presentation of the PATAGONIA TROUT project, issued under electronic corporate seal for evaluation by a financial investment partner. Confidential document; it does not constitute an offer of securities. Final terms are established exclusively in the definitive agreements between the parties.',
    },
    file: '/verify-docs/PT-MCG-2026-001.pdf',
  },
  'LOI-MCG-2026-001': {
    id: 'LOI-MCG-2026-001',
    ref: 'LOI/MCG/2026-001',
    type: {
      es: 'Carta de Intención de Compra (LOI) — Azúcar ICUMSA 45',
      en: 'Letter of Intent to Purchase (LOI) — ICUMSA 45 sugar',
    },
    issueDate: '2026-07-06',
    status: 'superseded',
    notes: {
      es: 'Carta de intención de compra emitida a un grupo productor de Brasil. Reemplazada por LOI/MCG/2026-002 del 21 de julio de 2026, que amplía el alcance a tres productos. No constituye obligación contractual; los términos definitivos se establecen en el contrato de compraventa.',
      en: 'Letter of intent to purchase issued to a producer group in Brazil. Superseded by LOI/MCG/2026-002 dated 21 July 2026, which extends the scope to three products. Non-binding; final terms to be set in the sale and purchase agreement.',
    },
  },
  'LOI-MCG-2026-002': {
    id: 'LOI-MCG-2026-002',
    ref: 'LOI/MCG/2026-002',
    type: {
      es: 'Carta de Intención de Compra (LOI) Multiproducto — Azúcar ICUMSA 45, Aceite de Soja Refinado y Café',
      en: 'Multi-Product Letter of Intent to Purchase (LOI) — ICUMSA 45 Sugar, Refined Soybean Oil and Coffee',
    },
    issueDate: '2026-07-21',
    status: 'valid',
    notes: {
      es: 'Carta de intención de compra multiproducto emitida a un grupo productor de Brasil. Reemplaza a LOI/MCG/2026-001 del 6 de julio de 2026. No constituye obligación contractual; los términos definitivos se establecen en el contrato de compraventa.',
      en: 'Multi-product letter of intent to purchase issued to a producer group in Brazil. Supersedes LOI/MCG/2026-001 dated 6 July 2026. Non-binding; final terms to be set in the sale and purchase agreement.',
    },
  },
  // --- Serie MCG-LNG-2026: cartas de intención de compra de langostino argentino
  // congelado con destino Estados Unidos, emitidas el 29-jul-2026 a ocho pesqueras.
  // OJO: NO se completa "issuedTo". El registro es público y enumerar las ocho
  // referencias revelaría a cada pesquera con quién más está hablando MERCALIA.
  ...Object.fromEntries(
    ['01', '02', '03', '04', '05', '06', '07', '08'].map((n) => [
      `MCG-LNG-2026-${n}`,
      {
        id: `MCG-LNG-2026-${n}`,
        ref: `MCG-LNG-2026-${n}`,
        type: {
          es: 'Carta de Intención de Compra (LOI) — Langostino argentino congelado, destino Estados Unidos',
          en: 'Letter of Intent to Purchase (LOI) — Argentine frozen shrimp, United States market',
        },
        issueDate: '2026-07-29',
        status: 'valid' as DocStatus,
        notes: {
          es: 'Carta de intención de compra emitida a una empresa pesquera argentina, con su Anexo A — Cuestionario de Calificación de Proveedor como parte integrante. No constituye obligación de compra ni de venta; los términos definitivos se establecen en el contrato de compraventa. Vigencia: treinta (30) días corridos desde la fecha de emisión.',
          en: 'Letter of intent to purchase issued to an Argentine fishing company, including Annex A — Supplier Qualification Questionnaire as an integral part. Non-binding; final terms to be set in the sale and purchase agreement. Validity: thirty (30) calendar days from the issue date.',
        },
      },
    ]),
  ),
  'CIS-MCG-2026-001': {
    id: 'CIS-MCG-2026-001',
    ref: 'CIS/MCG/2026-001',
    type: {
      es: 'Company Information Sheet (CIS) — Hoja de información corporativa',
      en: 'Company Information Sheet (CIS)',
    },
    issueDate: '2026-07-06',
    status: 'superseded',
    notes: {
      es: 'Documento de identificación corporativa emitido para fines de KYC y compliance. Reemplazado por CIS/MCG/2026-002 del 21 de septiembre de 2026, que incorpora el número D-U-N-S, el permiso IFTP de NOAA, el programa de verificación de proveedores y las líneas de producto vigentes.',
      en: 'Corporate identification document issued for KYC and compliance purposes. Superseded by CIS/MCG/2026-002 dated 21 September 2026, which adds the D-U-N-S number, the NOAA IFTP permit, the supplier verification program and the current product lines.',
    },
  },
  'CIS-MCG-2026-002': {
    id: 'CIS-MCG-2026-002',
    ref: 'CIS/MCG/2026-002',
    type: {
      es: 'Company Information Sheet (CIS) — Hoja de información corporativa',
      en: 'Company Information Sheet (CIS)',
    },
    issueDate: '2026-09-21',
    status: 'valid',
    notes: {
      es: 'Documento de identificación corporativa emitido para fines de identificación de contraparte, KYC y compliance. Reemplaza a CIS/MCG/2026-001 del 6 de julio de 2026. No constituye oferta, compromiso ni contrato de ninguna clase.',
      en: 'Corporate identification document issued for counterparty identification, KYC and compliance purposes. Supersedes CIS/MCG/2026-001 dated 6 July 2026. It does not constitute an offer, a commitment or a contract of any kind.',
    },
  },
};
